import { useState, useEffect, useRef } from 'react'
import { User, Edit, History, Settings, Camera } from 'lucide-react'
import { useUser } from '../contexts/UserContext'
import { getTransactions, updateProfile, uploadProfilePhoto, Transaction } from '../api/profile'
import { getMyAcceptedDonations, getMyCreatedDonations, Donation } from '../api/donations'
import { resolveImageUrl } from '../utils/imageUrl'

const ProfilePage = () => {
  const { user: contextUser, refreshUser } = useUser()
  const [donations, setDonations] = useState<Donation[]>([])
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('profile')
  const [isEditing, setIsEditing] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [photoMessage, setPhotoMessage] = useState<string | null>(null)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  })

  const formatStatus = (status: string): string => {
    return status
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ')
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Use context user as primary source
        if (contextUser) {
          setEditForm({
            firstName: contextUser.firstName,
            lastName: contextUser.lastName,
            phone: contextUser.phone,
            email: contextUser.email
          })
        }

        // Fetch transactions and donations
        const [transactionsData, donationsData] = await Promise.all([
          getTransactions(),
          contextUser?.userType === 'VOLUNTEER' 
            ? getMyAcceptedDonations()
            : contextUser?.userType === 'SUPPLIER' || contextUser?.userType === 'FOOD_BANK'
            ? getMyCreatedDonations()
            : Promise.resolve([])
        ])
        
        setTransactions(transactionsData)
        setDonations(donationsData)
      } catch (error) {
        console.error('Error fetching profile data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [contextUser])

  const handleSaveProfile = async () => {
    try {
      const updatedProfile = await updateProfile(editForm)
      if (updatedProfile) {
        // Refresh user context to get updated profile
        await refreshUser()
        setIsEditing(false)
      }
    } catch (error) {
      console.error('Error updating profile:', error)
    }
  }

  const handleDirectoryPhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPhotoMessage(null)
    setUploadingPhoto(true)
    try {
      await uploadProfilePhoto(file)
      await refreshUser()
      setPhotoMessage('Directory photo updated. It will appear on the public directory.')
    } catch (error: any) {
      setPhotoMessage(error?.message || 'Failed to upload photo.')
    } finally {
      setUploadingPhoto(false)
      if (photoInputRef.current) photoInputRef.current.value = ''
    }
  }

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'DONATION':
        return '🎁'
      case 'PICKUP':
        return '🚚'
      case 'VOLUNTEER_TRIP':
        return '🤝'
      default:
        return '📋'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading profile...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!contextUser) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-600">Please log in to view your profile</p>
          </div>
        </div>
      </div>
    )
  }

  const profile = contextUser
  const canUploadDirectoryPhoto =
    profile.userType === 'FOOD_BANK' || profile.userType === 'SUPPLIER'
  const directoryLabel =
    profile.userType === 'FOOD_BANK' ? 'Food Banks directory' : 'Suppliers directory'

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center space-x-6">
            <div className="flex-shrink-0 relative">
              {profile.profileImage ? (
                <img
                  src={resolveImageUrl(profile.profileImage)}
                  alt={`${profile.firstName} ${profile.lastName}`}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-2xl font-medium">
                    {getInitials(profile.firstName, profile.lastName)}
                  </span>
                </div>
              )}
              {canUploadDirectoryPhoto && (
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="absolute -bottom-1 -right-1 rounded-full bg-white border border-gray-200 p-1.5 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                  title="Upload directory photo"
                >
                  <Camera className="h-4 w-4 text-gray-700" />
                </button>
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">
                {profile.organizationName || `${profile.firstName} ${profile.lastName}`}
              </h1>
              <p className="text-gray-600">{profile.email}</p>
              <p className="text-sm text-gray-500 capitalize">
                {profile.userType.replace('_', ' ')} • Member since {formatDate(profile.createdAt)}
              </p>
              {canUploadDirectoryPhoto && (
                <p className="text-xs text-gray-500 mt-2">
                  {uploadingPhoto
                    ? 'Uploading directory photo…'
                    : `This photo appears on the public ${directoryLabel}.`}
                </p>
              )}
              {photoMessage && (
                <p className="text-xs text-primary-700 mt-1">{photoMessage}</p>
              )}
            </div>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="btn-secondary flex items-center"
            >
              <Edit className="w-4 h-4 mr-2" />
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>
          {canUploadDirectoryPhoto && (
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleDirectoryPhotoChange}
            />
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <User className="w-4 h-4 inline mr-2" />
                Profile
              </button>
              <button
                onClick={() => setActiveTab('transactions')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'transactions'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <History className="w-4 h-4 inline mr-2" />
                Transaction History
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'settings'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Settings className="w-4 h-4 inline mr-2" />
                Settings
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'profile' && (
              <div className="space-y-6">
                {canUploadDirectoryPhoto && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h3 className="text-sm font-semibold text-gray-900">Directory photo</h3>
                    <p className="text-xs text-gray-600 mt-1">
                      Upload a photo of your organization. This is what visitors see on the public{' '}
                      {directoryLabel}.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-4">
                      {profile.profileImage ? (
                        <img
                          src={resolveImageUrl(profile.profileImage)}
                          alt="Directory"
                          className="h-24 w-36 rounded-md object-cover border border-gray-200 bg-white"
                        />
                      ) : (
                        <div className="h-24 w-36 rounded-md border border-dashed border-gray-300 bg-white flex items-center justify-center text-xs text-gray-400">
                          No photo yet
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        className="btn-outline text-sm disabled:opacity-50"
                      >
                        {uploadingPhoto ? 'Uploading…' : profile.profileImage ? 'Replace photo' : 'Upload photo'}
                      </button>
                    </div>
                  </div>
                )}
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">First Name</label>
                        <input
                          type="text"
                          value={editForm.firstName}
                          onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="form-label">Last Name</label>
                        <input
                          type="text"
                          value={editForm.lastName}
                          onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                          className="input-field"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        value={editForm.phone}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        className="input-field"
                      />
                    </div>
                    <div className="flex space-x-4">
                      <button onClick={handleSaveProfile} className="btn-primary">
                        Save Changes
                      </button>
                      <button onClick={() => setIsEditing(false)} className="btn-outline">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">First Name</label>
                        <p className="text-gray-900">{profile.firstName}</p>
                      </div>
                      <div>
                        <label className="form-label">Last Name</label>
                        <p className="text-gray-900">{profile.lastName}</p>
                      </div>
                    </div>
                    <div>
                      <label className="form-label">Email</label>
                      <p className="text-gray-900">{profile.email}</p>
                    </div>
                    <div>
                      <label className="form-label">Phone</label>
                      <p className="text-gray-900">{profile.phone}</p>
                    </div>
                    <div>
                      <label className="form-label">User Type</label>
                      <p className="text-gray-900 capitalize">{profile.userType.replace('_', ' ')}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'transactions' && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Activity History</h3>
                
                {/* Show donations if available */}
                {donations.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">
                      {profile.userType === 'VOLUNTEER' ? 'Accepted Deliveries' : 'Created Donations'}
                    </h4>
                    <div className="space-y-3">
                      {donations.map((donation) => (
                        <div key={donation.id} className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="text-2xl mr-4">
                            {donation.status === 'DELIVERED' ? '✅' : donation.status === 'PICKED_UP' ? '🚚' : '📦'}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{donation.title}</h4>
                            <p className="text-sm text-gray-600">{donation.description}</p>
                            <p className="text-xs text-gray-500">
                              {formatDate(donation.createdAt)} • {donation.estimatedWeight}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              donation.status === 'DELIVERED' 
                                ? 'bg-green-100 text-green-800'
                                : donation.status === 'PICKED_UP'
                                ? 'bg-yellow-100 text-yellow-800'
                                : donation.status === 'ACCEPTED'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {formatStatus(donation.status)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show transactions if available */}
                {transactions.length > 0 && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Transactions</h4>
                    <div className="space-y-3">
                      {transactions.map((transaction) => (
                        <div key={transaction.id} className="flex items-center p-4 bg-gray-50 rounded-lg">
                          <div className="text-2xl mr-4">
                            {getTransactionIcon(transaction.type)}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{transaction.title}</h4>
                            <p className="text-sm text-gray-600">{transaction.description}</p>
                            <p className="text-xs text-gray-500">{formatDate(transaction.createdAt)}</p>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              transaction.status === 'COMPLETED' 
                                ? 'bg-green-100 text-green-800'
                                : transaction.status === 'PENDING'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {transaction.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {donations.length === 0 && transactions.length === 0 && (
                  <div className="text-center py-8">
                    <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No activity history yet</p>
                    <p className="text-sm text-gray-500 mt-2">
                      {profile.userType === 'VOLUNTEER' 
                        ? 'Start accepting deliveries to see your activity here'
                        : 'Create donations to see your activity here'}
                    </p>
                  </div>
                )}
              </div>
            )}


            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Account Settings</h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">Change Password</h4>
                      <p className="text-sm text-gray-600">Password changes are managed through Firebase Authentication.</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">Privacy Settings</h4>
                      <p className="text-sm text-gray-600">Your privacy is important to us. Contact support for privacy-related requests.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProfilePage 